import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import ServiceStore from "../../../stores/ServiceStore";

class HardwareTrackingStore {
  // Observables
  isLoading = false;
  error: string | null = null;
  filters = {
    jobNumber: "",
    hardwareType: "",
    status: "",
    supplier: "",
  };
  selectedHardwareIds: string[] = [];
  activityCounts: Map<number, number> = new Map();

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  updateFilter(field: string, value: any) {
    this.filters = { ...this.filters, [field]: value };
  }

  toggleHardwareSelection(hardwareId: string) {
    const index = this.selectedHardwareIds.indexOf(hardwareId);
    if (index > -1) {
      this.selectedHardwareIds.splice(index, 1);
    } else {
      this.selectedHardwareIds.push(hardwareId);
    }
  }

  clearSelection() {
    this.selectedHardwareIds = [];
  }

  // Async actions
  async loadHardwareData() {
    this.setLoading(true);
    this.setError(null);

    try {
      // Load hardware orders
      await (this.rootStore.hardwareOrdersStore as ServiceStore).find({
        query: {
          $limit: 1000,
          $sort: { id: -1 },
        },
      });

      // Load field activities
      await (this.rootStore.fieldActivityStore as ServiceStore).find({
        query: {
          $limit: 1000,
        },
      });

      // Load hardware-activity relations
      await (
        this.rootStore.fieldActivityHardwareOrderRelationStore as ServiceStore
      ).find({
        query: {
          $limit: 1000,
        },
      });

      // Calculate activity counts
      runInAction(() => {
        this.calculateActivityCounts();
      });
    } catch (error) {
      console.error("Error loading hardware data:", error);
      runInAction(() => {
        this.setError("Failed to load hardware data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  calculateActivityCounts() {
    this.activityCounts.clear();
    const relations = (
      this.rootStore.fieldActivityHardwareOrderRelationStore as ServiceStore
    ).entities;

    relations.forEach((relation: any) => {
      const count = this.activityCounts.get(relation.hardwareOrderId) || 0;
      this.activityCounts.set(relation.hardwareOrderId, count + 1);
    });
  }

  async generateQRCode(hardwareOrderId: string) {
    try {
      console.log(`Generating QR code for hardware order: ${hardwareOrderId}`);
      const qrData = {
        hardwareOrderId,
        url: `${window.location.origin}/field-reporting/hardware/${hardwareOrderId}`,
      };

      alert(
        `QR Code generated for Hardware Order: ${hardwareOrderId}\nData: ${JSON.stringify(
          qrData
        )}`
      );
    } catch (error) {
      console.error("Error generating QR code:", error);
      runInAction(() => {
        this.setError("Failed to generate QR code");
      });
    }
  }

  async generateBulkQRCodes() {
    if (this.selectedHardwareIds.length === 0) {
      return;
    }

    try {
      console.log(
        `Generating QR codes for ${this.selectedHardwareIds.length} hardware orders`
      );
      for (const hardwareId of this.selectedHardwareIds) {
        await this.generateQRCode(hardwareId);
      }
      runInAction(() => {
        this.clearSelection();
      });
    } catch (error) {
      console.error("Error generating bulk QR codes:", error);
      runInAction(() => {
        this.setError("Failed to generate bulk QR codes");
      });
    }
  }

  async exportHardwareData() {
    try {
      const hardware = this.filteredHardware;
      const csvContent = this.convertToCSV(hardware);
      this.downloadCSV(csvContent, "hardware_tracking.csv");
    } catch (error) {
      console.error("Error exporting hardware data:", error);
      runInAction(() => {
        this.setError("Failed to export hardware data");
      });
    }
  }

  async openActivityDialog(hardwareOrderId: number) {
    try {
      // Open dialog to add a new field activity
      this.rootStore.routerStore?.openDialog({
        dialogName: "FieldActivityForm",
        dialogContext: hardwareOrderId.toString(),
      });
    } catch (error) {
      console.error("Error opening activity dialog:", error);
    }
  }

  async viewActivityHistory(hardwareOrderId: number) {
    try {
      // Navigate to activity history view
      this.rootStore.routerStore?.goTo("FieldActivityHistory", {
        hardwareOrderId,
      });
    } catch (error) {
      console.error("Error viewing activity history:", error);
    }
  }

  async openNewHardwareDialog() {
    try {
      this.rootStore.routerStore?.openDialog({
        dialogName: "HardwareOrderForm",
      });
    } catch (error) {
      console.error("Error opening new hardware dialog:", error);
    }
  }

  async editHardwareOrder(hardwareOrderId: number) {
    try {
      this.rootStore.routerStore?.openDialog({
        dialogName: "HardwareOrderForm",
        dialogContext: hardwareOrderId.toString(),
      });
    } catch (error) {
      console.error("Error editing hardware order:", error);
    }
  }

  // Helper methods
  get filteredHardware() {
    let hardware = (this.rootStore.hardwareOrdersStore as ServiceStore)
      .entities;

    // Add activity counts to hardware data
    hardware = hardware.map((h: any) => ({
      ...h,
      activityCount: this.activityCounts.get(h.id) || 0,
    }));

    if (this.filters.jobNumber) {
      hardware = hardware.filter((h: any) =>
        h.jobNumber?.toString().includes(this.filters.jobNumber)
      );
    }

    if (this.filters.hardwareType) {
      hardware = hardware.filter((h: any) =>
        h.hardwareType
          ?.toLowerCase()
          .includes(this.filters.hardwareType.toLowerCase())
      );
    }

    if (this.filters.status) {
      hardware = hardware.filter((h: any) => h.status === this.filters.status);
    }

    if (this.filters.supplier) {
      hardware = hardware.filter((h: any) =>
        h.supplier?.toLowerCase().includes(this.filters.supplier.toLowerCase())
      );
    }

    return hardware;
  }

  convertToCSV(data: any[]) {
    if (data.length === 0) return "";

    const headers = [
      "Order ID",
      "Hardware Type",
      "Job #",
      "Panel ID",
      "Quantity",
      "Status",
      "Order Date",
      "Delivery Date",
      "Install Date",
      "Supplier",
      "Order #",
      "Activities",
    ];

    const rows = data.map((hardware) => [
      hardware.id || "",
      hardware.hardwareType || "",
      hardware.jobNumber || "",
      hardware.panelId || "",
      hardware.quantity || "",
      hardware.status || "",
      hardware.orderDate || "",
      hardware.deliveryDate || "",
      hardware.installDate || "",
      hardware.supplier || "",
      hardware.orderNumber || "",
      hardware.activityCount || "0",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    return csvContent;
  }

  downloadCSV(content: string, filename: string) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Computed values
  get totalHardwareOrders() {
    return (this.rootStore.hardwareOrdersStore as ServiceStore).entities.length;
  }

  get filteredHardwareCount() {
    return this.filteredHardware.length;
  }

  get totalActivities() {
    return (this.rootStore.fieldActivityStore as ServiceStore).entities.length;
  }
}

export default HardwareTrackingStore;
